/**
 * Tenant Store (Zustand)
 *
 * Global state management for multi-tenancy:
 * - Current tenant context
 * - Available tenants
 * - Tenant switching
 * - Tenant persistence
 *
 * @see WICL-DOC-001-A Multi-Tenant Architecture
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { type TenantRole, type TenantStatus } from "@/types/tenant";

/**
 * Tenant interface
 */
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  role: TenantRole;
  status: TenantStatus;
  plan?: string;
  // Encryption key metadata
  encryptedDataKey?: string | null;
  dataKeyCreatedAt?: string | null;
  dataKeyVersion?: number;
  createdAt: string;
}

export type { TenantRole, TenantStatus };

/**
 * Tenant Store State
 */
interface TenantState {
  // State
  currentTenant: Tenant | null;
  tenants: Tenant[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setTenant: (tenant: Tenant) => void;
  loadTenants: (tenants: Tenant[]) => void;
  switchTenant: (tenantId: string) => void;
  clearTenants: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/**
 * Tenant Store
 */
export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentTenant: null,
      tenants: [],
      isLoading: false,
      error: null,

      /**
       * Set current tenant
       */
      setTenant: (tenant: Tenant) => {
        set({ currentTenant: tenant });
      },

      /**
       * Load available tenants
       */
      loadTenants: (tenants: Tenant[]) => {
        set({ tenants });

        // If no current tenant and tenants available, set first one as current
        const { currentTenant } = get();
        if (!currentTenant && tenants.length > 0) {
          set({ currentTenant: tenants[0] });
        }
      },

      /**
       * Switch to different tenant
       */
      switchTenant: (tenantId: string) => {
        const { tenants } = get();
        const tenant = tenants.find((t) => t.id === tenantId);

        if (tenant) {
          set({ currentTenant: tenant });
        } else {
          console.error(`Tenant with ID ${tenantId} not found`);
        }
      },

      /**
       * Clear tenant state
       */
      clearTenants: () => {
        set({
          currentTenant: null,
          tenants: [],
          isLoading: false,
          error: null,
        });
      },

      /**
       * Set loading state
       */
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      /**
       * Set error state
       */
      setError: (error: string | null) => {
        set({ error });
      },
    }),
    {
      name: "wicloud_tenant",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentTenant: state.currentTenant,
        tenants: state.tenants,
      }),
    },
  ),
);

/**
 * Multi-tab synchronization
 * Listen to storage events to sync tenant state across tabs
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "wicloud_tenant") {
      if (event.newValue === null) {
        // Tenant cleared in another tab
        useTenantStore.getState().clearTenants();
      } else {
        // Tenant updated in another tab - sync state
        try {
          const newState = JSON.parse(event.newValue);
          useTenantStore.setState({
            currentTenant: newState.state.currentTenant,
            tenants: newState.state.tenants,
          });
        } catch (error) {
          console.error("Failed to sync tenant state:", error);
        }
      }
    }
  });
}
